import * as Yup from "yup";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import HelpIcon from "@material-ui/icons/Help";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useEffect, useState } from "react";
import { Field, Form, Formik, FormikHelpers } from "formik";

import BaseInput from "../../../../../components/elements/BaseInput";
import Button from "../../../../../components/elements/Button";
import CheckBox from "../../../../../components/elements/CheckBox";
import CurrencyFormatInput from "../../../../../components/elements/CurrencyFormatInput";
import DatePicker from "../../../../../components/elements/DatePicker";
import FormFieldError from "../../../../../components/elements/FormFieldError";
import Select from "../../../../../components/elements/Select";
import api from "../../../../../api";
import classes from "../../CampaignsDashboard.module.scss";
import { BrandgenCampaignPool, CampaignMediaTypeEnum } from "../../../../../types/campaignTypes";
import { MAX_CAMPAIGN_NAME_LENGTH } from "../../../../../constants/campaignValidation";
import {
  calculateEstimatedBudget,
  endDateCheckErrorsOnChange,
  maxBidCheckErrorsOnChange,
  startDateCheckErrorsOnChange,
} from "../../../../../utils/yupValidationUtils";
import { mediaTypeOptions } from "../../../../../views/CampaignCreationBasicSlides/AdGroupSelectionSlide/components/MediaTypeSelector/MediaTypeSelector";

const tooltips = {
  showInHubspot:
    "Checking this option will allow a HubSpot user to add and remove a Company to the Campaign. This will be available to All HubSpot users within your account who have permissions to access the Company Record",
  mediaType: (
    <>
      <Typography color="inherit">Please Note</Typography>
      <br />
      Currently, you can only choose display campaigns (image/gif) <em>or</em> video campaigns.
      <br />
      <br />
      <b>If you select video,</b> you will only be able to upload video creatives later in the campaign creation
      process.
    </>
  ),
  maxCompanies:
    "The maximum number of target companies that can be added to this campaign. If left empty or at 0, no limit will be imposed.",
  estimatedBudget: "The Estimated Budget is the Daily Budget multiplied by the number of days the campaign is running.",
};

type EditCampaignDetailsModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  campaignToEdit: BrandgenCampaignPool;
  fetchAndSetCampaings: () => void;
};

type FormInitialValues = {
  name: string;
  description: string;
  mediaType: "video" | "display";
  dailyBudget: number | string;
  estimatedBudget: number | string;
  maxBid: number | string;
  startDate: string | Date;
  endDate: string | Date;
  showInHubspot: boolean;
  maxCompanies: number | string;
  isActive: boolean;
};

const EditCampaignDetailsModal: FC<EditCampaignDetailsModalProps> = ({
  isOpen,
  handleClose,
  campaignToEdit,
  fetchAndSetCampaings,
}) => {
  const formInitialValues: FormInitialValues = {
    name: campaignToEdit.name,
    description: campaignToEdit.description ?? "",
    mediaType: campaignToEdit.isVideoCampaign ? CampaignMediaTypeEnum.VIDEO : CampaignMediaTypeEnum.DISPLAY,
    dailyBudget: campaignToEdit.daily_budget,
    estimatedBudget: campaignToEdit.campaign_budget,
    maxBid: campaignToEdit.maxBid === null ? 0 : campaignToEdit.maxBid,
    startDate: new Date(campaignToEdit.start_date).toString(),
    endDate: new Date(campaignToEdit.end_date).toString(),
    showInHubspot: campaignToEdit.showInHubspot,
    maxCompanies: campaignToEdit.maxCompanies,
    isActive: campaignToEdit.active,
  };

  const validationSchema = Yup.object().shape(
    {
      name: Yup.string()
        .required("Required field.")
        .test(
          "pixel-length-check",
          (value) =>
            `Campaign name is too long. Maximum length is ${MAX_CAMPAIGN_NAME_LENGTH} characters (current: ${
              value?.value?.length || 0
            }). `,
          (value) => {
            if (!value) return true;
            return `${value} Pixel`.length <= 100;
          },
        ),
      mediaType: Yup.string().required("Required field."),
      dailyBudget: Yup.number().positive("Number must be positive").required("Required field."),
      maxBid: Yup.number()
        .positive("Number must be positive!")
        .max(100, "Max Bid can not exceed $100")
        .when("estimatedBudget", (estimatedBudget: number, schema: any) => {
          return schema.max(estimatedBudget, "Max Bid can not be greater than the Estimated Budget");
        })
        .required("Required field."),
      startDate: Yup.date().when("startDate", (val: any, schema: any) => {
        const startDateTime = new Date(val).getTime();
        const todayDateTime = new Date().getTime();
        const isStartDateInFuture = startDateTime > todayDateTime;

        if (isStartDateInFuture) {
          return schema.min(
            new Date(new Date().setDate(new Date().getDate() - 1)),
            "Please choose a future or current date!",
          );
        }
        return schema;
      }),
      endDate: Yup.date().when("startDate", (startDate: any, schema: any) => {
        if (startDate) {
          const dayAfter = new Date(startDate.getTime() + 86400000);
          return (
            schema.min(dayAfter, "The end date must be after the start date") &&
            schema.min(
              new Date(new Date().setDate(new Date().getDate() - 1)),
              "Please choose a future or current date!",
            )
          );
        }

        return schema;
      }),
    },
    [["startDate", "startDate"]],
  );

  const startDateTime = new Date(formInitialValues.startDate).getTime();
  const todayDateTime = new Date().getTime();
  const isStartDateInFuture = startDateTime > todayDateTime;

  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  const handleSubmit = async (values: FormInitialValues, actions: FormikHelpers<FormInitialValues>) => {
    try {
      setIsLoading(true);
      const {
        name,
        description,
        mediaType,
        dailyBudget,
        estimatedBudget,
        maxBid,
        startDate,
        endDate,
        showInHubspot,
        maxCompanies,
        isActive,
      } = values;
      await api.updateCampaign(campaignToEdit.brandgenCampaignPoolId, {
        name,
        description,
        daily_budget: +dailyBudget,
        campaign_budget: +estimatedBudget,
        maxBid: +maxBid,
        start_date: startDate.toString(),
        // typeof startDate === "string" ? new Date(Date.parse(startDate)) : startDate,
        end_date: endDate.toString(),
        // typeof endDate === "string" ? new Date(Date.parse(endDate)) : endDate,
        active: isActive,
        maxCompanies: +maxCompanies,
        showInHubspot,
        isVideoCampaign: mediaType === "video",
      });
      setIsLoading(false);
      handleClose();
      fetchAndSetCampaings();
    } catch (error) {
      const err = error as any;
      setIsLoading(false);
      actions.resetForm();
      setErrorMessage(`There was an error: ${err.message}`);
    }
  };

  useEffect(() => {
    const today = new Date();
    const targetDate = new Date(campaignToEdit.end_date);

    today.setHours(0, 0, 0, 0);
    targetDate.setHours(0, 0, 0, 0);

    const diffTime = today.getTime() - targetDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays >= 175) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [campaignToEdit.end_date]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="Campaign Edit Modal"
      className={classes.modalContainer}
      disableEnforceFocus
    >
      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnBlur={false}
      >
        {({ setFieldValue, values, setFieldError }) => (
          <>
            <div className={classes.dialogTitle}>
              <Typography variant="h2">Edit Campaign Details</Typography>
            </div>
            <Form>
              <DialogContent className={classes.dialogContent}>
                <div className={classes.checkboxInput}>
                  <Field
                    as={FormControlLabel}
                    name="showInHubspot"
                    type="checkbox"
                    control={<CheckBox checked={values.showInHubspot} label="Sales Enabled Campaign" />}
                    disabled={isDisabled}
                  />

                  <Tooltip className={classes.tooltip} title={tooltips.showInHubspot}>
                    <HelpIcon />
                  </Tooltip>
                </div>

                <div className={classes.input}>
                  <Field
                    as={BaseInput}
                    value={values.name}
                    error={!!errorMessage}
                    name="name"
                    variant="outlined"
                    label="Name *"
                    disabled={isDisabled}
                  />

                  <FormFieldError name="name" />
                </div>

                <div className={classes.textArea}>
                  <Field
                    as={BaseInput}
                    value={values.description}
                    name="description"
                    variant="outlined"
                    label="Description"
                    rows={4}
                    multiline
                    disabled={isDisabled}
                  />

                  <FormFieldError name="description" />
                </div>

                <div className={classes.twoColumnInputs}>
                  <div className={classes.inputWithTooltip}>
                    <div className={classes.input}>
                      <Field
                        as={Select}
                        value={values.mediaType}
                        name="mediaType"
                        label="Campaign Media Type"
                        options={mediaTypeOptions}
                        disabled
                      />
                    </div>

                    <Tooltip className={classes.tooltip} title={tooltips.mediaType}>
                      <HelpIcon />
                    </Tooltip>
                  </div>

                  {values.showInHubspot ? (
                    <div className={classes.inputWithTooltip}>
                      <div className={classes.input}>
                        <Field
                          as={BaseInput}
                          value={values.maxCompanies}
                          name="maxCompanies"
                          variant="outlined"
                          label="Max Companies"
                          type="number"
                          InputProps={{
                            inputProps: {
                              min: 0,
                            },
                          }}
                          disabled={isDisabled}
                        />

                        <FormFieldError name="maxCompanies" />
                      </div>
                      <Tooltip className={classes.tooltip} title={tooltips.maxCompanies}>
                        <HelpIcon />
                      </Tooltip>
                    </div>
                  ) : null}
                </div>

                <div className={classes.twoColumnInputs}>
                  <div className={classes.input}>
                    <Field
                      as={DatePicker}
                      value={values.startDate}
                      variant="outlined"
                      label="Campaign Start Date *"
                      InputProps={{
                        name: "startDate",
                      }}
                      onChange={(date: Date) => {
                        setFieldValue("startDate", date.toString(), false);
                        const budget = calculateEstimatedBudget(date, values.endDate as Date, +values.dailyBudget);
                        setFieldValue("estimatedBudget", budget, false);
                        startDateCheckErrorsOnChange(date, setFieldError);
                        endDateCheckErrorsOnChange(date, values.endDate as Date, setFieldError);
                        maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                      }}
                      disabled={!isStartDateInFuture || isDisabled}
                    />
                    <FormFieldError name="startDate" />
                  </div>

                  <div className={classes.input}>
                    <Field
                      as={DatePicker}
                      value={values.endDate}
                      variant="outlined"
                      label="Campaign End Date *"
                      InputProps={{
                        name: "endDate",
                      }}
                      onChange={(date: Date) => {
                        setFieldValue("endDate", (date as Date).toString(), false);
                        const budget = calculateEstimatedBudget(values.startDate as Date, date, +values.dailyBudget);
                        setFieldValue("estimatedBudget", budget, false);
                        endDateCheckErrorsOnChange(values.startDate as Date, date, setFieldError);
                        maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                      }}
                      disabled={isDisabled}
                    />
                    <FormFieldError name="endDate" />
                  </div>
                </div>

                <div className={classes.twoColumnInputs}>
                  <div className={classes.input}>
                    <Field
                      as={CurrencyFormatInput}
                      value={values.dailyBudget}
                      name="dailyBudget"
                      variant="outlined"
                      label="Daily Budget $ *"
                      InputLabelProps={{ shrink: !!values.dailyBudget.toString() }}
                      onChange={({ target }: { target: any }) => {
                        const budget = calculateEstimatedBudget(
                          values.startDate as Date,
                          values.endDate as Date,
                          +target.value,
                        );
                        setFieldValue("estimatedBudget", budget, false);
                        setFieldValue("dailyBudget", target.value, false);
                        maxBidCheckErrorsOnChange(values.maxBid as number, budget, setFieldError);
                        if ((target.value as number) > 0 && target.value) {
                          setFieldError("dailyBudget", undefined);
                        }
                      }}
                      disabled={isDisabled}
                    />

                    <FormFieldError name="dailyBudget" />
                  </div>

                  <div className={classes.estimatedBudgetWithTooltip}>
                    <div className={classes.input}>
                      <Field
                        as={CurrencyFormatInput}
                        value={values.estimatedBudget}
                        name="estimatedBudget"
                        variant="outlined"
                        label="Estimated Budget $ *"
                        disabled
                        InputLabelProps={{ shrink: !!values.estimatedBudget.toString() }}
                      />
                    </div>

                    <Tooltip className={classes.tooltip} title={tooltips.estimatedBudget}>
                      <HelpIcon />
                    </Tooltip>
                  </div>

                  <div className={`${classes.inputMaxBid} ${classes.input}`}>
                    <Field
                      as={CurrencyFormatInput}
                      value={values.maxBid}
                      name="maxBid"
                      variant="outlined"
                      label="Max Bid $ *"
                      InputLabelProps={{ shrink: !!values.maxBid.toString() }}
                      onChange={({ target }: any) => {
                        setFieldValue("maxBid", target.value, false);
                        maxBidCheckErrorsOnChange(
                          target.value as number,
                          values.estimatedBudget as number,
                          setFieldError,
                        );
                      }}
                      disabled={isDisabled}
                    />

                    <FormFieldError name="maxBid" />
                  </div>
                </div>

                <div className={classes.activeCheckbox}>
                  <div className={classes.checkboxInput}>
                    <Field
                      as={FormControlLabel}
                      name="isActive"
                      type="checkbox"
                      control={<CheckBox checked={values.isActive} label="Active" disabled={isDisabled} />}
                    />
                  </div>
                </div>

                {isDisabled && (
                  <p className={classes.errorText}>
                    This campaign was deactivated over 175 days ago and can no longer be reactivated. Please create a
                    new campaign instead.
                  </p>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose} variant="outlined">
                  Cancel
                </Button>
                <Button type="submit" isLoading={isLoading} loaderSize={25} disabled={isDisabled}>
                  Submit
                </Button>
              </DialogActions>
            </Form>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditCampaignDetailsModal;
